/**
 * CMA PDF Generator
 * Handles PDF generation specifically for CMA
 */

import {
  formatDate,
  handleImageUrl,
  formatPhoneNumber,
  getCompChartData,
} from '../utils/pdfUtils'
import {
  currencyFormat,
  numberFormat,
  percentageFormat,
} from '@/utils/number'

import {
  startCase,
  get,
  sum,
  map,
  slice,
  meanBy,
  filter,
  chunk,
  isEmpty,
  minBy,
  maxBy,
  head,
  concat,
} from '@/utils/lodash'
import { BasePDFGenerator } from './BasePDFGenerator'

export class CMAPDFGenerator extends BasePDFGenerator {

  /**
   * Generate the complete CMA PDF document
   */
  async generatePDF() {
    const { property, urlAffiliate, affiliate, urlDomain } = this

    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      images = [],
      proformaImages = [],
      portfolio = {},
    } = property
    const { organization = {} } = portfolio
    const { CMALegalDescription = '' } = organization

    const { comps = [] } = this

    const whiteImage = await handleImageUrl('/images/white.jpg')

    const orgEmail = this.session.me?.username
      ? this.session.me?.username
      : organization.email
        ? organization.email
        : 'Email Not Entered'
    const orgPhone = this.session.me?.phone
      ? formatPhoneNumber(this.session.me?.phone)
      : organization.phone
        ? formatPhoneNumber(organization.phone)
        : 'Phone Not Entered'
    let orgLogo = property.proformaLogo
      ? await handleImageUrl(property.proformaLogo)
      : organization.logo
        ? organization.logo.url
          ? await handleImageUrl(organization.logo.url)
          : whiteImage
        : whiteImage

    if (!orgLogo) orgLogo = whiteImage

    let mainImage = ''

    if (proformaImages.length <= 0) {
      if (images.length <= 0) {
        mainImage = await handleImageUrl('/images/default_image.png')
      } else {
        mainImage = await handleImageUrl(images[0].url)
      }
    } else {
      mainImage = await handleImageUrl(proformaImages[0])
    }

    const compValues = await Promise.all(
      map(slice(comps, 0, 20), async (comp) => {
        return {
          _id: comp._id,
          address: comp.address,
          city: comp.city,
          state: comp.state,
          zip: comp.zip,
          beds: comp.beds,
          baths: comp.baths,
          propertyPrice: Number(
            comp.estval * (1 + comp.priceAdjustment / 100),
          ),
          soldDate: formatDate(comp.saleDate),
          soldPrice: Number(comp.estval),
          priceAdjustmentPercent: comp.priceAdjustment / 100,
          priceAdjustmentDollar: Number(
            (comp.estval * comp.priceAdjustment) / 100,
          ),
          propertyPerSqft: comp.sqft
            ? Number(comp.estval * (1 + comp.priceAdjustment / 100)) /
                comp.sqft || 0
            : null,
          sqft: comp.sqft,
          daysOnMarket: comp.daysOnMarket,
          image: isEmpty(comp.images)
            ? await handleImageUrl('/images/default_image.png')
            : await handleImageUrl(head(comp.images).url),
          initialListPrice: comp.initialListPrice,
        }
      }),
    )

    const averageCompValues = meanBy(
      filter(compValues, 'propertyPrice'),
      'propertyPrice',
    )

    const chartDataURLs = map(chunk(compValues, 4), (compGroup) =>
      getCompChartData(property, compGroup),
    )

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [0, 0, 0, 0],
      info: {
        // HIDE ADDRESS SETUP
        // title: property.proformaHideAddress ? `${startCase(property.city)}-${startCase(property.state)}-${property.zip}-finance` : `${property.address}-${startCase(property.city)}-${startCase(property.state)}-${property.zip}-finance`,
        title: `${property.address}-${startCase(
          property.city,
        )}-${startCase(property.state)}-${property.zip}-CMA`,
      },
      content: [
        {
          canvas: [
            {
              type: 'rect',
              x: 52,
              y: 42,
              w: 500,
              h: 52,
              r: 2,
              color: 'black',
              fillOpacity: 0.5,
            },
            {
              type: 'rect',
              x: 50,
              y: 40,
              w: 500,
              h: 52,
              r: 2,
            },
            {
              type: 'rect',
              x: 50,
              y: 40,
              w: 500,
              h: 52,
              r: 2,
              border: [true, true, true, true],
              color: 'white',
              fillOpacity: 1,
            },
          ],
        },
        {
          absolutePosition: { x: 125, y: 50 },
          stack: [
            { text: 'Comparative Market Analysis', fontSize: 28 },
          ],
        },
        {
          text: `${property.address} ${property.city}, ${property.state} ${property.zip}`,
          bold: true,
          fontSize: 20,
          margin: [50, 10, 50, 0],
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 50,
              y1: 5,
              x2: 550,
              y2: 5,
              lineWidth: 1,
              color: 'grey',
            },
          ],
        },
        {
          columns: [
            {
              image: mainImage,
              width: 250,
              height: 180,
              margin: [50, 5, 5, 5],
            },
            {
              width: 250,
              stack: [
                { text: 'Prepared By', bold: true },
                {
                  text: `${this.session.me?.profile.firstname} ${this.session.me?.profile.lastname}`,
                },
                { text: orgPhone },
                { text: orgEmail },
              ],
              margin: [60, 5, 5, 5],
            },
          ],
        },
        {
          absolutePosition: { x: 5, y: 755 },
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 5,
              x2: 580,
              y2: 5,
              lineWidth: 2,
            },
          ],
          margin: [0, 0, 0, 0],
        },
        {
          absolutePosition: { x: 5, y: 760 },
          stack: [
            {
              table: {
                headerRows: 1,
                widths: [70, 100, '*', 180],
                body: [
                  [
                    {
                      text: 'Powered By ',
                      alignment: 'left',
                      color: '#8e9194',
                      margin: [0, 10, 0, 0],
                    },
                    {
                      image: await handleImageUrl(
                        urlAffiliate
                          ? get(
                              affiliate,
                              'sidebar.sidebarMemberFullImage.0.url',
                            ) || '/images/NewLogoBlue.png'
                          : '/images/NewLogoBlue.png',
                      ),
                      width: 100,
                      height: 20,
                      margin: [-5, 7, 0, 0],
                    },
                    {
                      text: orgPhone,
                      alignment: 'right',
                      margin: [0, 10, 0, 0],
                      // margin:
                      //   orgEmail.length > 30
                      //     ? [0, 4, 0, 0]
                      //     : [0, 0, 0, 0],
                    },
                    {
                      image: orgLogo,
                      fit: [125, 50],
                      // width: 150,
                      // height: 75,
                      margin: [0, 0, 0, 0],
                      alignment: 'center',
                      rowSpan: 2,
                    },
                  ],
                  [
                    {
                      text: 'Create Your CMA at',
                      alignment: 'left',
                      margin: [0, -3, 0, 0],
                      color: '#8e9194',
                      fontSize: 8,
                      noWrap: true,
                      //colSpan: 1,
                    },
                    {
                      text: urlDomain || 'housefolios.com',
                      link:
                        'https://' +
                        (urlDomain || 'www.housefolios.com') +
                        '/',
                      alignment: 'left',
                      decoration: 'underline',
                      margin: [0, -3, 0, 0],
                      color: '#3399ff',
                      fontSize: 8,
                      //colSpan: 1,
                    },
                    {
                      text: orgEmail,
                      alignment: 'right',
                      margin: [0, -5, 0, 0],
                      fontSize:
                        orgEmail.length > 20
                          ? orgEmail.length > 30
                            ? 8
                            : 10
                          : 12,
                      // margin:
                      //   orgEmail.length > 30
                      //     ? [0, 4, 0, 0]
                      //     : [0, 0, 0, 0],
                    },
                  ],
                ],
              },
              margin: [5, 10, 10, 0],
              layout: {
                defaultBorder: false,
              },
            },
            {
              margin: [15, 10, 15, 15],
              color: 'gray',
              fontSize: 8,
              text: CMALegalDescription ? CMALegalDescription : '',
              pageBreak: 'after',
              //'The above agent/brokerage makes no warranty or representation about the content of this brochure. While the information displayed herein is thought to be accurate, it is your responsibility to independently confirm its accuracy and completeness. Any projections, opinions, assumptions or estimates are used for example only and do not represent the current or future performance of the property. The above agent/brokerage neither practices accounting nor gives advice regarding tax benefits/liabilities or any other tax, accounting or financial consideration, nor does the above agent/brokerage give advice regarding financial investments. It is strongly recommended that you seek appropriate professional counsel regarding your rights as a homeowner.',
            },
            {
              canvas: [
                {
                  type: 'rect',
                  x: 52,
                  y: 42,
                  w: 500,
                  h: 52,
                  r: 2,
                  color: 'black',
                  fillOpacity: 0.5,
                },
                {
                  type: 'rect',
                  x: 50,
                  y: 40,
                  w: 500,
                  h: 52,
                  r: 2,
                },
                {
                  type: 'rect',
                  x: 50,
                  y: 40,
                  w: 500,
                  h: 52,
                  r: 2,
                  border: [true, true, true, true],
                  color: 'white',
                  fillOpacity: 1,
                },
              ],
            },
            {
              absolutePosition: { x: 200, y: 50 },
              stack: [{ text: 'Subject Property', fontSize: 28 }],
            },
            {
              text: 'Subject Property Details',
              fontSize: 16,
              margin: [50, 10, 50, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 50,
                  y1: 5,
                  x2: 550,
                  y2: 5,
                  lineWidth: 1,
                  color: 'grey',
                },
              ],
            },
            {
              columns: [
                {
                  width: 250,
                  stack: [
                    {
                      image: mainImage,
                      width: 250,
                      height: 180,
                      margin: [50, 5, 5, 30],
                    },
                    {
                      margin: [50, 0, 0, 0],
                      table: {
                        headerRows: 1,
                        widths: [100, 130],
                        body: [
                          [
                            {
                              text: 'Subject Property Overview',
                              colSpan: 2,
                              alignment: 'center',
                            },
                            '',
                          ],
                          [
                            'List Price',
                            {
                              text: currencyFormat.format(
                                property.acquisition.analysis
                                  .listPrice,
                              ),
                              alignment: 'right',
                            },
                          ],
                          [
                            'Bedrooms',
                            { text: sum(beds), alignment: 'right' },
                          ],
                          [
                            'Bathrooms',
                            { text: sum(baths), alignment: 'right' },
                          ],
                          [
                            'Apx Total SqFt',
                            {
                              text: numberFormat.format(sum(sqft)),
                              alignment: 'right',
                            },
                          ],
                          ['Sold Date', ''],
                        ],
                      },
                    },
                  ],
                },
                {
                  width: 250,
                  stack: [
                    { text: 'Subject Address:', bold: true },
                    { text: property.address },
                    {
                      text: `${property.city}, ${property.state} ${property.zip}`,
                      margin: [0, 0, 0, 30],
                    },
                    { text: 'Suggested List Price', bold: true },
                    {
                      text: !isNaN(averageCompValues)
                        ? currencyFormat.format(averageCompValues)
                        : 'Subject property has no comps for comparision',
                    },
                  ],
                  margin: [60, 5, 5, 5],
                },
              ],
            },
            {
              pageBreak: 'before',
              canvas: [
                {
                  type: 'rect',
                  x: 52,
                  y: 42,
                  w: 500,
                  h: 52,
                  r: 2,
                  color: 'black',
                  fillOpacity: 0.5,
                },
                {
                  type: 'rect',
                  x: 50,
                  y: 40,
                  w: 500,
                  h: 52,
                  r: 2,
                },
                {
                  type: 'rect',
                  x: 50,
                  y: 40,
                  w: 500,
                  h: 52,
                  r: 2,
                  border: [true, true, true, true],
                  color: 'white',
                  fillOpacity: 1,
                },
              ],
            },
            {
              absolutePosition: { x: 175, y: 50 },
              stack: [
                { text: 'Sold Comparable Stats', fontSize: 28 },
              ],
            },
            {
              text: 'Sold Properties Comparable Stats',
              fontSize: 16,
              margin: [50, 10, 50, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 50,
                  y1: 5,
                  x2: 550,
                  y2: 5,
                  lineWidth: 1,
                  color: 'grey',
                },
              ],
            },
            {
              margin: [50, 10, 50, 0],
              columns: [
                {
                  stack: [
                    {
                      text: [
                        { text: 'Lowest List Price: ', bold: true },
                        '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Lowest Sold Price: ', bold: true },
                        !isEmpty(filter(compValues, 'soldPrice'))
                          ? currencyFormat.format(
                              get(
                                minBy(
                                  filter(compValues, 'soldPrice'),
                                  'soldPrice',
                                ),
                                'soldPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        {
                          text: 'Lowest Adjusted Price: ',
                          bold: true,
                        },
                        !isEmpty(filter(compValues, 'propertyPrice'))
                          ? currencyFormat.format(
                              get(
                                minBy(
                                  filter(compValues, 'propertyPrice'),
                                  'propertyPrice',
                                ),
                                'propertyPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Lowest Sqft: ', bold: true },
                        !isEmpty(filter(compValues, 'sqft'))
                          ? numberFormat.format(
                              get(
                                minBy(
                                  filter(compValues, 'sqft'),
                                  'sqft',
                                ),
                                'sqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Lowest $/SqFt: ', bold: true },
                        !isEmpty(
                          filter(compValues, 'propertyPerSqft'),
                        )
                          ? currencyFormat.format(
                              get(
                                minBy(
                                  filter(
                                    compValues,
                                    'propertyPerSqft',
                                  ),
                                  'propertyPerSqft',
                                ),
                                'propertyPerSqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Lowest DOM: ', bold: true },
                        !isEmpty(filter(compValues, 'daysOnMarket'))
                          ? get(
                              minBy(
                                filter(compValues, 'daysOnMarket'),
                                'daysOnMarket',
                              ),
                              'daysOnMarket',
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                  ],
                },
                {
                  stack: [
                    {
                      text: [
                        { text: 'Highest List Price: ', bold: true },
                        '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Highest Sold Price: ', bold: true },
                        !isEmpty(filter(compValues, 'soldPrice'))
                          ? currencyFormat.format(
                              get(
                                maxBy(
                                  filter(compValues, 'soldPrice'),
                                  'soldPrice',
                                ),
                                'soldPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        {
                          text: 'Highest Adjusted Price: ',
                          bold: true,
                        },
                        !isEmpty(filter(compValues, 'propertyPrice'))
                          ? currencyFormat.format(
                              get(
                                maxBy(
                                  filter(compValues, 'propertyPrice'),
                                  'propertyPrice',
                                ),
                                'propertyPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Highest Sqft: ', bold: true },
                        !isEmpty(filter(compValues, 'sqft'))
                          ? numberFormat.format(
                              get(
                                maxBy(
                                  filter(compValues, 'sqft'),
                                  'sqft',
                                ),
                                'sqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Highest $/SqFt: ', bold: true },
                        !isEmpty(
                          filter(compValues, 'propertyPerSqft'),
                        )
                          ? currencyFormat.format(
                              get(
                                maxBy(
                                  filter(
                                    compValues,
                                    'propertyPerSqft',
                                  ),
                                  'propertyPerSqft',
                                ),
                                'propertyPerSqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Highest DOM: ', bold: true },
                        !isEmpty(filter(compValues, 'daysOnMarket'))
                          ? get(
                              maxBy(
                                filter(compValues, 'daysOnMarket'),
                                'daysOnMarket',
                              ),
                              'daysOnMarket',
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                  ],
                },
                {
                  stack: [
                    {
                      text: [
                        { text: 'Average List Price: ', bold: true },
                        '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Average Sold Price: ', bold: true },
                        !isEmpty(filter(compValues, 'soldPrice'))
                          ? currencyFormat.format(
                              meanBy(
                                filter(compValues, 'soldPrice'),
                                'soldPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        {
                          text: 'Average Adjusted Price: ',
                          bold: true,
                        },
                        !isEmpty(filter(compValues, 'propertyPrice'))
                          ? currencyFormat.format(
                              meanBy(
                                filter(compValues, 'propertyPrice'),
                                'propertyPrice',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Average Sqft: ', bold: true },
                        !isEmpty(filter(compValues, 'sqft'))
                          ? numberFormat.format(
                              meanBy(
                                filter(compValues, 'sqft'),
                                'sqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Average $/SqFt: ', bold: true },
                        !isEmpty(
                          filter(compValues, 'propertyPerSqft'),
                        )
                          ? currencyFormat.format(
                              meanBy(
                                filter(compValues, 'propertyPerSqft'),
                                'propertyPerSqft',
                              ),
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: [
                        { text: 'Average DOM: ', bold: true },
                        !isEmpty(filter(compValues, 'daysOnMarket'))
                          ? meanBy(
                              filter(compValues, 'daysOnMarket'),
                              'daysOnMarket',
                            )
                          : '-',
                      ],
                      fontSize: 10,
                      margin: [0, 0, 0, 5],
                    },
                  ],
                },
              ],
            },
            {
              text: 'Subject Property',
              alignment: 'center',
              margin: [0, 30, 0, 0],
            },
            {
              margin: [50, 5, 50, 0],
              table: {
                widths: ['*', '*', '*', 'auto', '*'],
                headerRows: 1,

                body: [
                  [
                    {
                      text: 'Address',
                      alignment: 'center',
                      fontSize: 10,
                    },
                    {
                      text: 'City',
                      alignment: 'center',
                      fontSize: 10,
                    },
                    {
                      text: 'Apx SqFt',
                      alignment: 'center',
                      fontSize: 10,
                    },
                    {
                      text: 'Suggested List Price',
                      alignment: 'center',
                      fontSize: 10,
                    },
                    {
                      text: '$/SqFt',
                      alignment: 'center',
                      fontSize: 10,
                    },
                  ],
                  [
                    { text: property.address, fontSize: 10 },
                    { text: property.city, fontSize: 10 },
                    {
                      text: numberFormat.format(sum(property.sqft)),
                      alignment: 'center',
                      fontSize: 10,
                    },
                    {
                      text: !isNaN(averageCompValues)
                        ? currencyFormat.format(averageCompValues)
                        : '-',
                      alignment: 'right',
                      fontSize: 10,
                    },
                    {
                      text: sum(property.sqft)
                        ? currencyFormat.format(
                            averageCompValues / sum(property.sqft),
                          )
                        : '-',
                      alignment: 'right',
                      fontSize: 10,
                    },
                  ],
                ],
              },
            },
            {
              text: `${
                compValues ? compValues.length : 0
              } Sold Comparables`,
              alignment: 'center',
              margin: [0, 30, 0, 0],
            },
            {
              margin: [50, 5, 50, 0],
              table: {
                widths: [
                  'auto',
                  '*',
                  '*',
                  '*',
                  'auto',
                  'auto',
                  '*',
                  'auto',
                  'auto',
                ],
                headerRows: 1,
                body: concat(
                  [
                    [
                      {
                        text: 'Address',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'City',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'Apx SqFt',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'List Price',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'Sold Price',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'Adjusted Price',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: '$/SqFt',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'SP/LP',
                        alignment: 'center',
                        fontSize: 8,
                      },
                      {
                        text: 'DOM',
                        alignment: 'center',
                        fontSize: 8,
                      },
                    ],
                  ],
                  map(compValues, (comp) => [
                    { text: comp.address, fontSize: 8 },
                    { text: comp.city, fontSize: 8 },
                    {
                      text: numberFormat.format(comp.sqft),
                      alignment: 'center',
                      fontSize: 8,
                    },
                    { text: '-', alignment: 'right', fontSize: 8 },
                    {
                      text: currencyFormat.format(comp.soldPrice),
                      alignment: 'right',
                      fontSize: 8,
                    },
                    {
                      text: currencyFormat.format(comp.propertyPrice),
                      alignment: 'right',
                      fontSize: 8,
                    },
                    {
                      text: comp.sqft
                        ? currencyFormat.format(
                            comp.propertyPrice / comp.sqft,
                          )
                        : '-',
                      alignment: 'right',
                      fontSize: 8,
                    },
                    { text: '-', alignment: 'right', fontSize: 8 },
                    {
                      text: comp.daysOnMarket || '-',
                      alignment: 'right',
                      fontSize: 8,
                    },
                  ]),
                ),
              },
            },
            {
              stack: map(compValues, (comp, index) => [
                {
                  pageBreak: 'before',
                  canvas: [
                    {
                      type: 'rect',
                      x: 52,
                      y: 42,
                      w: 500,
                      h: 52,
                      r: 2,
                      color: 'black',
                      fillOpacity: 0.5,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                      border: [true, true, true, true],
                      color: 'white',
                      fillOpacity: 1,
                    },
                  ],
                },
                {
                  absolutePosition: { x: 175, y: 50 },
                  stack: [
                    {
                      text: `Sold Comparable ${index + 1}`,
                      fontSize: 28,
                    },
                  ],
                },
                {
                  text: 'Sold Comparable Property Details',
                  fontSize: 16,
                  margin: [50, 10, 50, 0],
                },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 50,
                      y1: 5,
                      x2: 550,
                      y2: 5,
                      lineWidth: 1,
                      color: 'grey',
                    },
                  ],
                },
                {
                  columns: [
                    {
                      width: 250,
                      stack: [
                        {
                          image: comp.image,
                          width: 250,
                          height: 180,
                          margin: [50, 5, 5, 30],
                        },
                        {
                          margin: [50, 0, 0, 0],
                          table: {
                            headerRows: 1,
                            widths: [100, 130],
                            body: [
                              [
                                {
                                  text: `Comp ${
                                    index + 1
                                  } Chosen Criteria`,
                                  alignment: 'center',
                                },
                                {
                                  text: 'Criteria Value',
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Listing Number',
                                { text: '-', alignment: 'center' },
                              ],
                              [
                                'List Price',
                                { text: '-', alignment: 'center' },
                              ],
                              [
                                'Original List Price',
                                {
                                  text: comp.initialListPrice
                                    ? currencyFormat.format(
                                        comp.initialListPrice,
                                      )
                                    : '-',
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Bedrooms',
                                {
                                  text: comp.beds,
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Bathrooms',
                                {
                                  text: comp.beds,
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Apx Total SqFt',
                                {
                                  text: numberFormat.format(
                                    comp.sqft,
                                  ),
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Sold Price',
                                {
                                  text: currencyFormat.format(
                                    comp.soldPrice,
                                  ),
                                  alignment: 'center',
                                },
                              ],
                              [
                                'Sold Date',
                                {
                                  text: comp.soldDate,
                                  alignment: 'center',
                                },
                              ],
                            ],
                          },
                        },
                      ],
                    },
                    {
                      width: 250,
                      stack: [
                        {
                          text: `Comparable ${index + 1} Address:`,
                          bold: true,
                        },
                        { text: property.address },
                        {
                          text: `${property.city}, ${property.state} ${property.zip}`,
                          margin: [0, 0, 0, 30],
                        },
                        {
                          text: [
                            { text: 'Sold Price: ', bold: true },
                            currencyFormat.format(comp.soldPrice),
                          ],
                        },
                        {
                          text: [
                            { text: 'Adjusted Price: ', bold: true },
                            currencyFormat.format(comp.propertyPrice),
                          ],
                        },
                        {
                          text: 'Public Remarks',
                          bold: true,
                          fontSize: 10,
                          margin: [0, 100, 0, 0],
                        },
                        {
                          text: '-',
                          bold: true,
                          fontSize: 10,
                          margin: [0, 5, 0, 0],
                        },
                      ],
                      margin: [60, 5, 5, 5],
                    },
                  ],
                },
              ]),
            },
            {
              stack: map(chunk(compValues, 2), (compPair, index) => [
                {
                  pageBreak: 'before',
                  canvas: [
                    {
                      type: 'rect',
                      x: 52,
                      y: 42,
                      w: 500,
                      h: 52,
                      r: 2,
                      color: 'black',
                      fillOpacity: 0.5,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                      border: [true, true, true, true],
                      color: 'white',
                      fillOpacity: 1,
                    },
                  ],
                },
                {
                  absolutePosition: { x: 150, y: 50 },
                  stack: [
                    {
                      text: 'Side by Side Comparison',
                      fontSize: 28,
                    },
                  ],
                },
                {
                  margin: [50, 20, 40, 0],
                  table: {
                    headerRows: 2,
                    widths: ['auto', '*', '*', '*'],
                    body: [
                      [
                        '',
                        {
                          image: mainImage,
                          width: 120,
                          alignment: 'center',
                        },
                        {
                          image: compPair[0].image,
                          width: 120,
                          alignment: 'center',
                        },
                        {
                          image:
                            compPair.length > 1
                              ? compPair[1].image
                              : whiteImage,
                          width: 120,
                          alignment: 'center',
                        },
                      ],
                      [
                        '',
                        { text: `Subject`, alignment: 'center' },
                        {
                          text: `Comparable ${index * 2 + 1}`,
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? `Comparable ${index * 2 + 2}`
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Address',
                        `${property.address} ${property.city}, ${property.state} ${property.zip}`,
                        `${compPair[0].address} ${compPair[0].city}, ${compPair[0].state} ${compPair[0].zip}`,
                        compPair.length > 1
                          ? `${compPair[1].address} ${compPair[1].city}, ${compPair[1].state} ${compPair[1].zip}`
                          : '',
                      ],
                      [
                        'Suggested List Price',
                        {
                          text: currencyFormat.format(
                            averageCompValues,
                          ),
                          alignment: 'right',
                        },
                        '',
                        '',
                      ],
                      [
                        'Adjusted Price',
                        '',
                        {
                          text: [
                            currencyFormat.format(
                              compPair[0].propertyPrice,
                            ),
                            {
                              text: `(${percentageFormat.format(
                                compPair[0].priceAdjustmentPercent,
                              )})`,
                            },
                          ],
                          alignment: 'right',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? [
                                  currencyFormat.format(
                                    compPair[1].propertyPrice,
                                  ),
                                  {
                                    text: `(${percentageFormat.format(
                                      compPair[1]
                                        .priceAdjustmentPercent,
                                    )})`,
                                  },
                                ]
                              : '',
                          alignment: 'right',
                        },
                      ],
                      [
                        'List/Sell Price',
                        { text: '', alignment: 'center' },
                        { text: '-', alignment: 'center' },
                        {
                          text: compPair.length > 1 ? '-' : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Misc Adjustments',
                        '',
                        {
                          text: currencyFormat.format(
                            compPair[0].priceAdjustmentDollar,
                          ),
                          alignment: 'right',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? currencyFormat.format(
                                  compPair[1].priceAdjustmentDollar,
                                )
                              : '',
                          alignment: 'right',
                        },
                      ],
                      [
                        'List Price',
                        {
                          text: currencyFormat.format(
                            property.acquisition.analysis.listPrice,
                          ),
                          alignment: 'center',
                        },
                        { text: '-', alignment: 'center' },
                        { text: '-', alignment: 'center' },
                      ],
                      [
                        'Original List Price',
                        '',
                        {
                          text: compPair[0].initialListPrice
                            ? currencyFormat.format(
                                compPair[0].initialListPrice,
                              )
                            : '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? compPair[1].initialListPrice
                                ? currencyFormat.format(
                                    compPair[1].initialListPrice,
                                  )
                                : '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Bedrooms',
                        {
                          text: sum(beds) || '_',
                          alignment: 'center',
                        },
                        {
                          text: compPair[0].beds || '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? compPair[1].beds || '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Bathrooms',
                        {
                          text: sum(baths) || '_',
                          alignment: 'center',
                        },
                        {
                          text: compPair[0].baths || '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? compPair[1].baths || '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Apx Total SqFt',
                        {
                          text:
                            numberFormat.format(sum(property.sqft)) ||
                            '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            numberFormat.format(compPair[0].sqft) ||
                            '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? numberFormat.format(
                                  compPair[1].sqft,
                                ) || '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Sold Price',
                        '',
                        {
                          text:
                            currencyFormat.format(
                              compPair[0].soldPrice,
                            ) || '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? currencyFormat.format(
                                  compPair[1].soldPrice,
                                ) || '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                      [
                        'Sold Date',
                        '',
                        {
                          text: compPair[0].soldDate || '-',
                          alignment: 'center',
                        },
                        {
                          text:
                            compPair.length > 1
                              ? compPair[1].soldDate || '-'
                              : '',
                          alignment: 'center',
                        },
                      ],
                    ],
                  },
                },
              ]),
            },
            map(chartDataURLs, (chartDataURL) => ({
              stack: [
                {
                  pageBreak: 'before',
                  canvas: [
                    {
                      type: 'rect',
                      x: 52,
                      y: 42,
                      w: 500,
                      h: 52,
                      r: 2,
                      color: 'black',
                      fillOpacity: 0.5,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                    },
                    {
                      type: 'rect',
                      x: 50,
                      y: 40,
                      w: 500,
                      h: 52,
                      r: 2,
                      border: [true, true, true, true],
                      color: 'white',
                      fillOpacity: 1,
                    },
                  ],
                },
                {
                  absolutePosition: { x: 175, y: 50 },
                  stack: [
                    { text: 'Comparable Bar Chart', fontSize: 28 },
                  ],
                },
                {
                  image: chartDataURL,
                  width: 500,
                  height: 700,
                  alignment: 'center',
                  margin: [0, 20, 0, 0],
                },
              ],
            })),
          ],
        },
      ],
    }

    return docDefinition
  }
}
